const _excluded = ["onChange", "value"],
  _excluded2 = ["onChange"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { isEqual, range } from 'lodash-es';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useOverlayContext, Button, Card, InfoIcon, MenuItem, Page, Select, TextButton, TextField, Typography, Checkbox, WithInfoIcon } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { getTestIds } from 'seagull/utils/TestIds';
import { accountantAccessInfoTooltip } from "../../business-logic/accountantAccessLogic";
import { getAllAutoApprovalDays } from "../../business-logic/autoApprovalLogic";
import IntroVideoField from "../../components/AgreementStudioPage/AgreementStudioModal/IntroVideo/IntroVideoField";
import { normalizeVariableLabel, validateTemplateVariableLabel, validateTemplateVariableDefaultValue } from "../../components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils";
import { LegalTermsDropdown } from "../../components/LegalTermsDropdown/LegalTermsDropdown";
import { NavigationGuardModal } from "../../components/NavigationGuardModal";
import { businessSettingsDefaults, daysOfWeek, netTermsOptions } from "../../constants";
import { useStoreActions } from "../../contexts";
import { useLegalTerms } from "../../hooks/reactQuery/queries/useLegalTerms";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { useLocaleKeys } from "../../i18n";
import { sendErrReport } from "../../infra/utils";
import { BillingDayOfMonthModel, BillingDayOfMonthType, approvalMethodNoticePeriodModel } from "../../models";
import { makeExpirationDate } from "../../models/expiration-date";
import { RequestAccountantAccess, requestAccountantAccessModel } from "../../models/request-accountant-access";
import { selectors } from "../../store/state-selectors";
import { useStore } from "../../store/store-context";
import { StartBillingOnEnum } from "../../types/api.model";
import pluralize from "../../utils/pluralize";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import IntroductoryMessageEditor from "./IntroductoryMessageEditor/IntroductoryMessageEditor";
import styles from "./ProposalDefaults.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const testPrefix = 'settings-business-settings';
const updateLoader = 'updateBusinessSettings';
var FormFieldType = /*#__PURE__*/function (FormFieldType) {
  FormFieldType["Input"] = "input";
  FormFieldType["Heading"] = "heading";
  FormFieldType["Separator"] = "separator";
  FormFieldType["LegalTermsUpload"] = "legalTermsUpload";
  return FormFieldType;
}(FormFieldType || {});
const formId = 'business-settings-form';
export const {
  ProposalDefaultsTID
} = getTestIds();
export default function ProposalDefaults() {
  var _worksAt$settings$tem, _worksAt$settings, _worksAt$settings2, _worksAt$settings3, _worksAt$settings4, _worksAt$settings5, _worksAt$settings6, _worksAt$settings7, _worksAt$settings8, _worksAt$settings9, _worksAt$settings10, _worksAt$settings11, _worksAt$settings12, _worksAt$settings13, _worksAt$settings14, _worksAt$settings15, _worksAt$settings16, _worksAt$settings17, _worksAt$settings18, _worksAt$settings19, _worksAt$settings20, _worksAt$settings21;
  const worksAt = useStore(selectors.user.business);
  const {
    api,
    setWorksAt,
    fetchUserInfo
  } = useStoreActions();
  const {
    startLoader,
    stopLoader,
    showErrorToast
  } = useOverlayContext();
  const {
    legalTerms
  } = useLegalTerms({
    refreshOnHookMount: true,
    withLoader: true
  });
  const [isLegalTermsFieldDirty, setIsLegalTermsFieldDirty] = useState(false);
  const {
    t
  } = useLocaleKeys('pages.proposalDefaults');
  const {
    TEMPLATE_VARIABLES
  } = useFeatureFlags();
  const savedTemplateVariables = (_worksAt$settings$tem = worksAt === null || worksAt === void 0 || (_worksAt$settings = worksAt.settings) === null || _worksAt$settings === void 0 ? void 0 : _worksAt$settings.templateVariables) !== null && _worksAt$settings$tem !== void 0 ? _worksAt$settings$tem : [];
  const [templateVariables, setTemplateVariables] = useState(savedTemplateVariables.map(v => _objectSpread(_objectSpread({}, v), {}, {
    id: generateUniqueId('template-var')
  })));
  const isTemplateVariablesDirty = !isEqual(templateVariables.map(_ref => {
    let {
      label,
      defaultValue
    } = _ref;
    return {
      label,
      defaultValue
    };
  }), savedTemplateVariables);
  const updateVariableLabel = useCallback((index, label) => {
    setTemplateVariables(prev => prev.map((v, i) => i === index ? _objectSpread(_objectSpread({}, v), {}, {
      label
    }) : v));
  }, []);
  const updateVariableDefaultValue = useCallback((index, defaultValue) => {
    setTemplateVariables(prev => prev.map((v, i) => i === index ? _objectSpread(_objectSpread({}, v), {}, {
      defaultValue
    }) : v));
  }, []);
  const removeVariable = useCallback(index => {
    setTemplateVariables(prev => prev.filter((_, i) => i !== index));
  }, []);
  const addVariable = useCallback(() => {
    setTemplateVariables(prev => [...prev, {
      id: generateUniqueId('template-var'),
      label: '',
      defaultValue: ''
    }]);
  }, []);
  const billingDayOfMonthOptions = range(1, 29).map(day => BillingDayOfMonthModel.make(day)).concat([BillingDayOfMonthModel.make()]);
  const autoApprovalOptions = useMemo(() => {
    return [approvalMethodNoticePeriodModel.makeManualApprovalMethodNoticePeriod()].concat(getAllAutoApprovalDays().map(approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod));
  }, []);
  const startBillingOnMode = (worksAt === null || worksAt === void 0 || (_worksAt$settings2 = worksAt.settings) === null || _worksAt$settings2 === void 0 ? void 0 : _worksAt$settings2.startBillingOn) || businessSettingsDefaults.startBillingOn || StartBillingOnEnum.SkipBillingOnAcceptance;
  const selectedBillingDayOfMonth = (worksAt === null || worksAt === void 0 || (_worksAt$settings3 = worksAt.settings) === null || _worksAt$settings3 === void 0 ? void 0 : _worksAt$settings3.billingDayOfMonth) || businessSettingsDefaults.billingDayOfMonth;
  if (selectedBillingDayOfMonth.type === BillingDayOfMonthType.LastDayOfMonth) delete selectedBillingDayOfMonth['day'];
  const defaultValues = {
    legalTermsDocumentId: (worksAt === null || worksAt === void 0 || (_worksAt$settings4 = worksAt.settings) === null || _worksAt$settings4 === void 0 ? void 0 : _worksAt$settings4.legalTermsDocumentId) || businessSettingsDefaults.legalTermsDocumentId,
    disablePlaidLink: (worksAt === null || worksAt === void 0 || (_worksAt$settings5 = worksAt.settings) === null || _worksAt$settings5 === void 0 ? void 0 : _worksAt$settings5.disablePlaidLink) || businessSettingsDefaults.disablePlaidLink,
    netTerms: (worksAt === null || worksAt === void 0 || (_worksAt$settings6 = worksAt.settings) === null || _worksAt$settings6 === void 0 ? void 0 : _worksAt$settings6.netTerms) || businessSettingsDefaults.netTerms,
    billingDayOfMonth: selectedBillingDayOfMonth,
    billingDayOfWeek: (worksAt === null || worksAt === void 0 || (_worksAt$settings7 = worksAt.settings) === null || _worksAt$settings7 === void 0 ? void 0 : _worksAt$settings7.billingDayOfWeek) || businessSettingsDefaults.billingDayOfWeek,
    feeControls: {
      coverCreditCardFees: (worksAt === null || worksAt === void 0 || (_worksAt$settings8 = worksAt.settings) === null || _worksAt$settings8 === void 0 ? void 0 : _worksAt$settings8.feeControls.coverCreditCardFees) || businessSettingsDefaults.feeControls.coverCreditCardFees,
      coverDirectDebitFees: (worksAt === null || worksAt === void 0 || (_worksAt$settings9 = worksAt.settings) === null || _worksAt$settings9 === void 0 ? void 0 : _worksAt$settings9.feeControls.coverDirectDebitFees) || businessSettingsDefaults.feeControls.coverDirectDebitFees
    },
    amendmentsApprovalMethod: (worksAt === null || worksAt === void 0 || (_worksAt$settings10 = worksAt.settings) === null || _worksAt$settings10 === void 0 ? void 0 : _worksAt$settings10.amendmentsApprovalMethod) || businessSettingsDefaults.amendmentsApprovalMethod,
    invoicesApprovalMethod: (worksAt === null || worksAt === void 0 || (_worksAt$settings11 = worksAt.settings) === null || _worksAt$settings11 === void 0 ? void 0 : _worksAt$settings11.invoicesApprovalMethod) || businessSettingsDefaults.invoicesApprovalMethod,
    billUpfront: (worksAt === null || worksAt === void 0 || (_worksAt$settings12 = worksAt.settings) === null || _worksAt$settings12 === void 0 ? void 0 : _worksAt$settings12.billUpfront) || businessSettingsDefaults.billUpfront,
    automaticServicesPausedByDefault: (worksAt === null || worksAt === void 0 || (_worksAt$settings13 = worksAt.settings) === null || _worksAt$settings13 === void 0 ? void 0 : _worksAt$settings13.automaticServicesPausedByDefault) || businessSettingsDefaults.automaticServicesPausedByDefault,
    prorateFirstInvoice: (worksAt === null || worksAt === void 0 || (_worksAt$settings14 = worksAt.settings) === null || _worksAt$settings14 === void 0 ? void 0 : _worksAt$settings14.prorateFirstInvoice) || businessSettingsDefaults.prorateFirstInvoice,
    requestAccountantAccess: (worksAt === null || worksAt === void 0 || (_worksAt$settings15 = worksAt.settings) === null || _worksAt$settings15 === void 0 ? void 0 : _worksAt$settings15.requestAccountantAccess) || businessSettingsDefaults.requestAccountantAccess,
    introductoryMessages: {
      proposal: (worksAt === null || worksAt === void 0 || (_worksAt$settings16 = worksAt.settings) === null || _worksAt$settings16 === void 0 ? void 0 : _worksAt$settings16.introductoryMessages.proposal) || businessSettingsDefaults.introductoryMessages.proposal,
      invitation: (worksAt === null || worksAt === void 0 || (_worksAt$settings17 = worksAt.settings) === null || _worksAt$settings17 === void 0 ? void 0 : _worksAt$settings17.introductoryMessages.invitation) || businessSettingsDefaults.introductoryMessages.invitation
    },
    startBillingOn: (worksAt === null || worksAt === void 0 || (_worksAt$settings18 = worksAt.settings) === null || _worksAt$settings18 === void 0 ? void 0 : _worksAt$settings18.startBillingOn) || businessSettingsDefaults.startBillingOn,
    introVideo: (worksAt === null || worksAt === void 0 || (_worksAt$settings19 = worksAt.settings) === null || _worksAt$settings19 === void 0 ? void 0 : _worksAt$settings19.introVideo) || businessSettingsDefaults.introVideo,
    selectedTemplate: 'introductoryMessages.proposal',
    startPaused: startBillingOnMode === StartBillingOnEnum.StartPaused,
    billOnAcceptance: startBillingOnMode === StartBillingOnEnum.Earliest,
    expirationDate: (worksAt === null || worksAt === void 0 || (_worksAt$settings20 = worksAt.settings) === null || _worksAt$settings20 === void 0 ? void 0 : _worksAt$settings20.expirationDate) || businessSettingsDefaults.expirationDate,
    invoicePrefix: (worksAt === null || worksAt === void 0 || (_worksAt$settings21 = worksAt.settings) === null || _worksAt$settings21 === void 0 ? void 0 : _worksAt$settings21.invoicePrefix) || businessSettingsDefaults.invoicePrefix
  };
  delete defaultValues['defaultLegalTermsDocumentId'];
  delete defaultValues['defaultBillingDayOfMonth'];
  const {
    control,
    handleSubmit,
    formState,
    getValues,
    reset,
    setValue
  } = useForm({
    defaultValues,
    mode: 'onSubmit'
  });
  const [lastDayOptionDisabled, setLastDayOptionDisabled] = useState(getValues('billUpfront'));
  const billUpfront = useWatch({
    name: 'billUpfront',
    control
  });
  const startPaused = useWatch({
    name: 'startPaused',
    control
  });
  const billOnAcceptance = useWatch({
    name: 'billOnAcceptance',
    control
  });
  const prorateFirstInvoice = useWatch({
    name: 'prorateFirstInvoice',
    control
  });
  const startBillingOn = useWatch({
    name: 'startBillingOn',
    control
  });
  const introVideo = useWatch({
    name: 'introVideo',
    control
  });
  const billingDayOfMonth = useWatch({
    name: 'billingDayOfMonth.type',
    control
  });
  const legalTermsDocumentId = useWatch({
    name: 'legalTermsDocumentId',
    control
  });
  const [isProrationDisabled, setIsProrationDisabled] = useState(false);
  const [isStartPausedDisabled, setIsStartPausedDisabled] = useState(false);
  const [isBillOnAcceptanceDisabled, setIsBillOnAcceptanceDisabled] = useState(false);
  const billUpfrontErrorShown = billUpfront && billingDayOfMonth === BillingDayOfMonthType.LastDayOfMonth;
  useSetDocumentTitle(t.pageTitle());
  useEffect(() => {
    if (!billUpfront && startBillingOn === StartBillingOnEnum.Earliest) {
      setValue('startBillingOn', StartBillingOnEnum.SkipBillingOnAcceptance);
    }
  }, [billUpfront, startBillingOn]);
  useEffect(() => {
    const isProrationInvalid = startPaused || !billOnAcceptance && billUpfront;
    setIsProrationDisabled(isProrationInvalid);
    setValue('prorateFirstInvoice', isProrationInvalid ? false : prorateFirstInvoice);
  }, [startPaused, billOnAcceptance, billUpfront, setValue, prorateFirstInvoice]);
  useEffect(() => {
    const isStartPausedInvalid = billUpfront && billOnAcceptance;
    setIsStartPausedDisabled(isStartPausedInvalid);
    setValue('startPaused', isStartPausedInvalid ? false : startPaused);
  }, [billOnAcceptance, billUpfront, setValue, startPaused]);
  useEffect(() => {
    const isBillOnAcceptanceInvalid = startPaused || !billUpfront;
    setIsBillOnAcceptanceDisabled(isBillOnAcceptanceInvalid);
    setValue('billOnAcceptance', isBillOnAcceptanceInvalid ? false : billOnAcceptance);
  }, [startPaused, billUpfront, setValue, billOnAcceptance]);
  const selectedLegalTerms = useMemo(() => {
    return legalTerms.find(lt => lt.id === legalTermsDocumentId);
  }, [legalTermsDocumentId, legalTerms]);
  const fields = [{
    type: FormFieldType.Heading,
    label: t.sections.generalSettings()
  }, {
    type: FormFieldType.LegalTermsUpload,
    label: t.fields.termsOfService()
  }, {
    type: FormFieldType.Input,
    name: 'amendmentsApprovalMethod',
    label: t.fields.amendmentsApproval.label(),
    tooltip: t.fields.amendmentsApproval.tooltip(),
    options: autoApprovalOptions.map(option => ({
      value: option,
      label: approvalMethodNoticePeriodModel.isAutoApproval(option) ? t.options.enabledWithNoticeDays({
        days: pluralize('day', option.auto.noticePeriodDays, true)
      }) : t.options.disabled()
    }))
  }, {
    type: FormFieldType.Input,
    name: 'invoicesApprovalMethod',
    label: t.fields.invoicesApproval.label(),
    tooltip: t.fields.invoicesApproval.tooltip(),
    options: autoApprovalOptions.map(option => ({
      value: option,
      label: approvalMethodNoticePeriodModel.isAutoApproval(option) ? t.options.enabledWithNoticeDays({
        days: pluralize('day', option.auto.noticePeriodDays, true)
      }) : t.options.disabled()
    }))
  }, {
    type: FormFieldType.Input,
    name: 'netTerms',
    label: t.fields.netTerms.label(),
    options: netTermsOptions.map(numOfDays => ({
      label: numOfDays === 0 ? t.fields.netTerms.onAcceptance() : pluralize('day', numOfDays, true),
      value: numOfDays
    }))
  }, {
    type: FormFieldType.Input,
    name: 'feeControls.coverCreditCardFees',
    label: t.fields.creditCardFees.label(),
    options: [{
      label: t.fields.creditCardFees.theClient(),
      value: false
    }, {
      label: (worksAt === null || worksAt === void 0 ? void 0 : worksAt.name) || t.fields.creditCardFees.theVendor(),
      value: true
    }]
  }, {
    type: FormFieldType.Input,
    name: 'expirationDate',
    label: t.fields.expirationDate.label(),
    options: [{
      label: t.fields.expirationDate.noExpiration(),
      value: makeExpirationDate()
    }, ...range(1, 91).map(day => ({
      label: day + '',
      value: makeExpirationDate(day)
    }))]
  }, {
    type: FormFieldType.Input,
    name: 'requestAccountantAccess',
    label: t.fields.accountantAccess.label(),
    tooltip: accountantAccessInfoTooltip,
    isCheckbox: true,
    mapValue: value => requestAccountantAccessModel.wasRequested(value),
    mapOnChange: event => event.target.checked ? RequestAccountantAccess.Mandatory : RequestAccountantAccess.None
  }, {
    type: FormFieldType.Separator
  }, {
    type: FormFieldType.Heading,
    label: t.sections.automaticServicesSettings()
  }, {
    type: FormFieldType.Input,
    name: 'billUpfront',
    label: t.fields.billUpfront.label(),
    options: [{
      value: true,
      label: t.fields.billUpfront.upfront()
    }, {
      value: false,
      label: t.fields.billUpfront.endOfCycle()
    }]
  }, {
    type: FormFieldType.Input,
    name: 'billingDayOfMonth',
    label: t.fields.billingDayOfMonth.label(),
    tooltip: t.fields.billingDayOfMonth.tooltip(),
    options: billingDayOfMonthOptions.map(option => ({
      label: option.formatDay(t.fields.billingDayOfMonth.lastDay()),
      value: option,
      disabled: option.isLast() && lastDayOptionDisabled
    })),
    error: billUpfrontErrorShown,
    helperText: billUpfrontErrorShown ? t.fields.billingDayOfMonth.error() : ''
  }, {
    type: FormFieldType.Input,
    name: 'billingDayOfWeek',
    label: t.fields.billingDayOfWeek.label(),
    tooltip: t.fields.billingDayOfWeek.tooltip(),
    options: daysOfWeek.map(value => ({
      value,
      label: value
    }))
  }, {
    type: FormFieldType.Input,
    isCheckbox: true,
    name: 'startPaused',
    disabled: isStartPausedDisabled,
    label: t.fields.startPaused.label(),
    tooltip: t.fields.startPaused.tooltip(),
    options: [{
      label: t.options.disabled(),
      value: false
    }, {
      label: t.options.enabled(),
      value: true
    }]
  }, {
    type: FormFieldType.Input,
    isCheckbox: true,
    name: 'billOnAcceptance',
    disabled: isBillOnAcceptanceDisabled,
    label: t.fields.billOnAcceptance.label(),
    tooltip: t.fields.billOnAcceptance.tooltip(),
    options: [{
      label: t.options.disabled(),
      value: false
    }, {
      label: t.options.enabled(),
      value: true
    }]
  }, {
    type: FormFieldType.Input,
    isCheckbox: true,
    name: 'prorateFirstInvoice',
    label: t.fields.prorateFirstInvoice.label(),
    disabled: isProrationDisabled,
    tooltip: t.fields.prorateFirstInvoice.tooltip(),
    options: [{
      label: t.options.disabled(),
      value: false
    }, {
      label: t.options.enabled(),
      value: true
    }]
  }, {
    type: FormFieldType.Separator
  }];
  const onSubmit = async values => {
    if (!worksAt) return;
    if (billUpfrontErrorShown) return;
    const hasValidationErrors = templateVariables.some(v => validateTemplateVariableLabel(v.label) !== null || v.label.trim().length > 0 && validateTemplateVariableDefaultValue(v.defaultValue) !== null);
    if (hasValidationErrors) return;
    startLoader(updateLoader);
    const params = _objectSpread(_objectSpread({}, values), {}, {
      coverCreditCardFees: values.feeControls.coverCreditCardFees,
      startBillingOn: values.startPaused ? StartBillingOnEnum.StartPaused : values.billOnAcceptance ? StartBillingOnEnum.Earliest : StartBillingOnEnum.SkipBillingOnAcceptance
    }, TEMPLATE_VARIABLES ? {
      templateVariables: templateVariables.filter(v => v.label.trim().length > 0).reduce((acc, v) => {
        const normalized = normalizeVariableLabel(v.label);
        if (!acc.some(existing => normalizeVariableLabel(existing.label) === normalized)) {
          acc.push({
            label: v.label.trim(),
            defaultValue: v.defaultValue
          });
        }
        return acc;
      }, [])
    } : {});
    delete params['selectedTemplate'];
    delete params['feeControls'];
    delete params['disablePlaidLink'];
    delete params['startPaused'];
    delete params['billOnAcceptance'];
    try {
      await api.vendor.updateBusinessSettings(worksAt.id, params);
      const settings = await api.vendor.getBusinessSettings(worksAt.id);
      if (settings) {
        var _settings$templateVar;
        setWorksAt(Object.assign({}, worksAt, {
          settings
        }));
        const persistedVariables = (_settings$templateVar = settings.templateVariables) !== null && _settings$templateVar !== void 0 ? _settings$templateVar : [];
        setTemplateVariables(persistedVariables.map(v => _objectSpread(_objectSpread({}, v), {}, {
          id: generateUniqueId('template-var')
        })));
      }
      setIsLegalTermsFieldDirty(false);
      reset(undefined, {
        keepValues: true
      });
    } catch (error) {
      showErrorToast(t.errors.failedToSave(), 'save-error-toast');
      sendErrReport(error);
    }
    stopLoader(updateLoader);
  };
  const onDeleteLegalTerm = async legalTermsId => {
    const userInfo = await fetchUserInfo();
    if (legalTermsId === legalTermsDocumentId) {
      var _userInfo$business$se, _userInfo$business;
      const newLegalTermsDocumentId = (_userInfo$business$se = userInfo === null || userInfo === void 0 || (_userInfo$business = userInfo.business()) === null || _userInfo$business === void 0 || (_userInfo$business = _userInfo$business.settings) === null || _userInfo$business === void 0 ? void 0 : _userInfo$business.legalTermsDocumentId) !== null && _userInfo$business$se !== void 0 ? _userInfo$business$se : businessSettingsDefaults.legalTermsDocumentId;
      setValue('legalTermsDocumentId', newLegalTermsDocumentId, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  };
  return /*#__PURE__*/_jsxs(Page, {
    header: /*#__PURE__*/_jsx(Typography, {
      variant: "h5",
      children: t.pageHeader()
    }),
    subheader: /*#__PURE__*/_jsx(Typography, {
      children: t.pageSubheader()
    }),
    testId: testPrefix + '-screen',
    minWidth: "978px",
    actions: /*#__PURE__*/_jsx(Button, {
      loading: formState.isSubmitting,
      testId: testPrefix + '-btn-save-changes',
      form: formId,
      type: "submit",
      children: t.saveChanges()
    }),
    skin: "secondary",
    children: [/*#__PURE__*/_jsxs(Card, {
      autoHeight: true,
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.titles
      }), /*#__PURE__*/_jsxs("form", {
        id: formId,
        className: styles.form,
        onSubmit: handleSubmit(onSubmit),
        children: [fields.map((fieldConfig, idx) => {
          if (fieldConfig.type === FormFieldType.Input) {
            const {
              name,
              label,
              tooltip,
              options,
              error,
              helperText,
              isCheckbox,
              disabled,
              mapValue,
              mapOnChange
            } = fieldConfig;
            if (isCheckbox) {
              return /*#__PURE__*/_jsx("div", {
                className: cn(styles.fieldWithTooltip, styles.checkboxField),
                children: /*#__PURE__*/_jsx(Controller, {
                  control: control,
                  name: name,
                  render: _ref2 => {
                    let {
                        field: {
                          onChange: _onChange,
                          value
                        }
                      } = _ref2,
                      field = _objectWithoutProperties(_ref2.field, _excluded);
                    return tooltip ? /*#__PURE__*/_jsx(WithInfoIcon, {
                      info: tooltip,
                      children: /*#__PURE__*/_jsx(Checkbox, _objectSpread(_objectSpread({
                        testId: "".concat(testPrefix, "-").concat(name),
                        checked: mapValue ? mapValue(value) : value,
                        disabled: disabled,
                        onChange: event => _onChange(mapOnChange ? mapOnChange(event) : event)
                      }, field), {}, {
                        children: /*#__PURE__*/_jsx(Typography, {
                          skin: disabled ? 'disabled' : 'standard',
                          children: label
                        })
                      }), name)
                    }) : /*#__PURE__*/_jsx(Checkbox, _objectSpread(_objectSpread({
                      testId: "".concat(testPrefix, "-").concat(name),
                      checked: value,
                      disabled: disabled,
                      onChange: _onChange
                    }, field), {}, {
                      children: /*#__PURE__*/_jsx(Typography, {
                        skin: disabled ? 'disabled' : 'standard',
                        children: label
                      })
                    }), name);
                  }
                })
              }, name);
            } else {
              return /*#__PURE__*/_jsxs(Fragment, {
                children: [/*#__PURE__*/_jsxs("div", {
                  className: styles.fieldWithTooltip,
                  children: [/*#__PURE__*/_jsx(Typography, {
                    children: label
                  }), tooltip && /*#__PURE__*/_jsx(InfoIcon, {
                    info: tooltip
                  })]
                }), /*#__PURE__*/_jsx(Controller, {
                  control: control,
                  name: name,
                  render: props => {
                    const _connectControllerToT = connectControllerToTextField(props),
                      {
                        onChange: _onChange2
                      } = _connectControllerToT,
                      rest = _objectWithoutProperties(_connectControllerToT, _excluded2);
                    const value = getValues(name);
                    const isObject = typeof value === 'object';
                    const primitiveValue = isObject ? JSON.stringify(value) : value;
                    return /*#__PURE__*/_jsx(Select, _objectSpread(_objectSpread({
                      collapseHelperText: false
                    }, rest), {}, {
                      value: primitiveValue,
                      onChange: e => {
                        if (isObject) {
                          e.target.value = JSON.parse(e.target.value);
                        }
                        _onChange2(e);
                      },
                      menuMaxHeight: 300,
                      SelectProps: {
                        onOpen: () => {
                          setLastDayOptionDisabled(getValues('billUpfront'));
                        }
                      },
                      error: error,
                      helperText: helperText,
                      testId: "".concat(testPrefix, "-").concat(name),
                      children: options === null || options === void 0 ? void 0 : options.map((_ref3, i) => {
                        let {
                          value,
                          label,
                          disabled
                        } = _ref3;
                        const primitiveValue = typeof value === 'object' ? JSON.stringify(value) : value;
                        return /*#__PURE__*/_jsx(MenuItem, {
                          value: primitiveValue,
                          testId: "".concat(testPrefix, "-").concat(name, "-option-").concat(i),
                          disabled: disabled,
                          children: label
                        }, primitiveValue);
                      })
                    }));
                  }
                })]
              }, name);
            }
          } else if (fieldConfig.type === FormFieldType.Heading) {
            return /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx(Typography, {
                variant: "paragraph1",
                weight: "normal",
                children: fieldConfig.label
              }), /*#__PURE__*/_jsx("div", {})]
            }, idx);
          } else if (fieldConfig.type === FormFieldType.Separator) {
            return /*#__PURE__*/_jsx(Fragment, {
              children: /*#__PURE__*/_jsx("div", {
                className: styles.separator
              })
            }, idx);
          } else if (fieldConfig.type === FormFieldType.LegalTermsUpload) {
            return /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx(Typography, {
                children: fieldConfig.label
              }), /*#__PURE__*/_jsx(Controller, {
                control: control,
                name: "legalTermsDocumentId",
                rules: {
                  required: t.errors.pleaseChooseDocument()
                },
                render: _ref4 => {
                  var _formState$errors$leg;
                  let {
                    field,
                    formState
                  } = _ref4;
                  return /*#__PURE__*/_jsx(LegalTermsDropdown, {
                    inputRef: field.ref,
                    error: formState.isSubmitted && !formState.isSubmitSuccessful ? (_formState$errors$leg = formState.errors.legalTermsDocumentId) === null || _formState$errors$leg === void 0 ? void 0 : _formState$errors$leg.message : undefined,
                    onDeleteLegalTerm: onDeleteLegalTerm,
                    viewMode: "advanced",
                    testId: ProposalDefaultsTID.LegalTermsDropdown(),
                    selectedLegalTermId: selectedLegalTerms === null || selectedLegalTerms === void 0 ? void 0 : selectedLegalTerms.id,
                    onSelectLegalTerm: legalTerm => {
                      var _legalTerm$id;
                      setValue('legalTermsDocumentId', (_legalTerm$id = legalTerm === null || legalTerm === void 0 ? void 0 : legalTerm.id) !== null && _legalTerm$id !== void 0 ? _legalTerm$id : '', {
                        shouldValidate: true,
                        shouldDirty: true
                      });
                    }
                  });
                }
              })]
            }, idx);
          }
        }), /*#__PURE__*/_jsx(IntroductoryMessageEditor, {
          selectedTemplateFormFieldName: "selectedTemplate",
          templates: [{
            name: 'introductoryMessages.proposal',
            dropdownLabel: t.fields.introductoryMessages.newClientTemplate()
          }, {
            name: 'introductoryMessages.invitation',
            dropdownLabel: t.fields.introductoryMessages.existingClientTemplate()
          }],
          control: control,
          formState: formState
        }), TEMPLATE_VARIABLES && /*#__PURE__*/_jsxs("div", {
          className: styles.templateVariablesSection,
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "paragraph1",
            weight: "normal",
            children: t.fields.templateVariables.sectionTitle()
          }), /*#__PURE__*/_jsx("div", {}), templateVariables.map((variable, index) => {
            const hasLabel = variable.label.trim().length > 0;
            const labelError = validateTemplateVariableLabel(variable.label);
            const defaultValueError = hasLabel ? validateTemplateVariableDefaultValue(variable.defaultValue) : null;
            return /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx(TextField, {
                value: variable.label,
                onChange: e => updateVariableLabel(index, e.target.value),
                placeholder: t.fields.templateVariables.variableNamePlaceholder(),
                testId: ProposalDefaultsTID.VariableLabel(index),
                error: !!labelError,
                helperText: labelError !== null && labelError !== void 0 ? labelError : undefined
              }), /*#__PURE__*/_jsxs("div", {
                className: styles.templateVariableValueRow,
                children: [/*#__PURE__*/_jsx(TextField, {
                  value: variable.defaultValue,
                  onChange: e => updateVariableDefaultValue(index, e.target.value),
                  placeholder: t.fields.templateVariables.defaultValuePlaceholder(),
                  testId: ProposalDefaultsTID.VariableValue(index),
                  error: !!defaultValueError,
                  helperText: defaultValueError !== null && defaultValueError !== void 0 ? defaultValueError : undefined,
                  disabled: !hasLabel
                }), /*#__PURE__*/_jsx(TextButton, {
                  onClick: () => removeVariable(index),
                  testId: ProposalDefaultsTID.VariableRemove(index),
                  type: "button",
                  children: t.fields.templateVariables.removeButton()
                })]
              })]
            }, variable.id);
          }), /*#__PURE__*/_jsx(TextButton, {
            onClick: addVariable,
            testId: ProposalDefaultsTID.AddVariable(),
            type: "button",
            children: t.fields.templateVariables.addButton()
          }), /*#__PURE__*/_jsx("div", {})]
        }), /*#__PURE__*/_jsx(IntroVideoField, {
          introVideo: introVideo,
          onChange: introVideo => setValue('introVideo', introVideo, {
            shouldDirty: true
          }),
          className: styles.video
        })]
      })]
    }), /*#__PURE__*/_jsx(NavigationGuardModal, {
      when: formState.isDirty || isLegalTermsFieldDirty || isTemplateVariablesDirty,
      primaryButtonTestId: "business-settings-confirm-navigation",
      secondaryButtonTestId: "business-settings-keep-editing"
    })]
  });
}